// ── THE FALL FARMER CARD'S APPROVED YIELD — THE QUOTE'S RULE, NOT THE SUMMARY ROW ─────────
// BUG-096. The Fall farmer card printed the stored `crop_coverage` summary — for a crop year
// with no acreage reported yet, an UNWEIGHTED average of whatever databases the AIP has
// re-approved, which is the ground the grower did NOT plant:
//
//   CLARK FIVE ENTERPRISES, Huntingdon PA wheat, CY2027
//     CY2027 databases on file: 0003-0003 65 · 0004 74 · 0007 81 · 0008 80 — no acres
//     planted in 2026:          0003-0002 96 on 49.1 ac · 0006 103 on 47.3 · 0009 97 on 38.75
//     summary row 75.0 = (65 + 74 + 81 + 80) / 4           the card, as "AIP working: 75.0"
//     acre-weighted over the ground farmed: 98.7           the wheat quote and the printed quote
//
// ONE RULE, THREE SURFACES. This module calls the SAME functions the wheat quote's resolver
// does — unitDatabases / weightedDatabaseAph / resolveQuoteYields / aphBasisNote in
// ./aphDatabases.js — with the inputs the farmer card actually has: no APH edit, no database
// ticks, no planned acres, no roll. With those absent the quote's resolver reduces to exactly
// resolveQuoteYields({ stored, weighted }), which is what runs here.
//
// UNKNOWN STAYS UNKNOWN. Until the farm's databases have been read — and if they cannot be —
// a wheat row's approved yield is NULL with the reason, never the summary row: the summary is
// the very value in question, so falling back to it would print 75.0 again the first time the
// read is slow. A database with no CY2027 acres is weighed by its own reported 2026 acres
// (unitDatabases' `prev`), never by zero; where no database carries acres in either year the
// rule returns the stored row, exactly as the quote does, and the card says what it is.
//
// A FARM'S DATABASES BELONG TO THAT FARM. `unitsFor` hands back units only when they were
// loaded for the farmer being shown, so switching A -> B -> A cannot resolve B's rows with
// A's databases while B's read is in flight.
import { unitDatabases, weightedDatabaseAph, resolveQuoteYields, aphBasisNote } from "./aphDatabases.js";

// What the card reads — the columns unitDatabases / blendDatabases use, and nothing else.
export const FALL_APH_UNITS_SELECT =
  "policy_id,crop_year,county,crop,plan,practice_code,unit_number,sub_unit,acres,aph_yield,rate_yield,adjusted_yield,is_prevent_plant";

// state = { farmerId, status: "resolving" | "ready" | "error", units }
export function unitsFor(state, farmerId) {
  return state && state.farmerId === farmerId && state.status === "ready" && Array.isArray(state.units) ? state.units : null;
}
export function statusFor(state, farmerId) {
  return state && state.farmerId === farmerId ? state.status : "resolving";
}

const isEndorsementPlan = (p) => /SCO|ECO/i.test(String(p || ""));

// One row. `units` null means the databases are not (yet) known.
export function resolveFallRow(r, { year, units, status = "resolving" } = {}) {
  if (!r) return r;
  if (String(r.crop || "").toUpperCase() !== "WHEAT" || isEndorsementPlan(r.plan) || year == null) return r;
  const stored = r.approvedYield != null && +r.approvedYield > 0 ? +r.approvedYield : null;
  if (!Array.isArray(units)) {
    return { ...r, approvedYield: null,
             aph: { source: status === "error" ? "unreadable" : "resolving", stored, dbCount: null, basis: null } };
  }
  const opts = { county: String(r.county || "").toUpperCase(), practice: r.practiceCode || "003", curYear: +year };
  const yearOf = (u) => (u && u.crop_year != null ? +u.crop_year : null);
  const dbs = unitDatabases(units, yearOf, opts);
  const dbCount = dbs.length;
  // What the stored summary row actually averages: this year's databases. A prior-year-only entry
  // (a 2026 row with no unit number, say) is not one of them, so it must not inflate the label.
  const curDbCount = dbs.filter((d) => d.yieldsFrom === +year).length;
  const weighted = weightedDatabaseAph(units, yearOf, opts);
  const res = resolveQuoteYields({ stored: { aph: stored }, weighted });
  return { ...r, approvedYield: res.aph,
           aph: { source: res.source, stored, dbCount, curDbCount, basis: aphBasisNote({ source: res.source, blend: res.blend, quoteYear: +year }) } };
}

export function resolveFallRows(rows, ctx) {
  return (rows || []).map((r) => resolveFallRow(r, ctx));
}

// What the card prints under the figure, in words. Null where there is nothing to explain.
export function fallAphLabel(aph, year) {
  if (!aph) return null;
  if (aph.source === "resolving") return { text: "APH: reading the AIP's databases…", title: "The approved yield is resolved from the AIP's per-database figures; nothing is shown until they are read." };
  if (aph.source === "unreadable") return { text: "APH not resolved", title: "The AIP's per-database figures could not be read, so no approved yield is shown — the summary row is not the approved yield." };
  if (aph.source === "weighted") return { text: `Across databases: ${aph.basis || "acre-weighted"}`,
    title: `Acre-weighted across the AIP's APH databases, the ground farmed counting, as the wheat quote prices it.${aph.stored != null ? ` The CY${year} summary row (${aph.stored}) averages every database unweighted and is not the approved yield.` : ""}` };
  if (aph.curDbCount >= 2) return { text: `AIP summary — average of ${aph.curDbCount} databases, no acres to weight by`,
    title: "No database carries acres in either year, so there is nothing to weight by; this is the AIP file's summary row, as the wheat quote uses it." };
  return null;
}
